import { useRef, useState } from 'react';
import { AnimatePresence, Box, Link, ModuleBase, ResponsiveImage, Stack, Text } from '@/components';
import { ImageModel, LinkModel, ModuleBaseModel, ModuleBasePropsModel } from '@/types';
import { fadeIn, fadeInLeft, listingGridMotion } from '@/animations';
import classNames from 'classnames';
import DropdownArrow from '@/assets/caretDownLight.svg';
import { motion } from 'framer-motion';
import { usePathname } from 'next/navigation';

type CaseStudyModuleModel = ModuleBaseModel<
  {
    cards: {
      image: ImageModel;
      imageTitle?: string;
      link: LinkModel;
      preHeading?: string;
      title: string;
      text: string;
    }[];
    initalSelectedCard?: number;
  } & ModuleBasePropsModel
>;

export const motionImage = {
  variants: {
    inactive: {
      opacity: 0,
      scale: 0.95,
    },
    active: {
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 50,
      },
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      damping: 10,
      mass: 0.1,
      stiffness: 200,
      type: 'spring',
      duration: 0.1,
    },
  },
};

const motionText = (delay: number) => ({
  variants: {
    inactive: {
      x: 50,
      opacity: 0,
    },
    active: {
      x: 0,
      opacity: 1,
      transition: {
        delay,
        type: 'spring',
        damping: 25,
      },
    },
  },
  exit: {
    ...fadeInLeft.variants.inactive,
    transition: {
      damping: 10,
      mass: 0.1,
      stiffness: 100,
      type: 'spring',
    },
  },
});

const CaseStudyModule = (data: CaseStudyModuleModel) => {
  const { cards, initalSelectedCard } = data.props;
  const pathname = usePathname();

  const [activeCardIndex, setActiveCardIndex] = useState<number | null>(
    (initalSelectedCard && initalSelectedCard < cards.length ? initalSelectedCard : 0) || 0,
  );
  const [animation, setAnimation] = useState<object | undefined>(undefined);
  const nextActiveCardIndex = useRef<number>(0);

  const _setActiveCard = (direction: number) => {
    const newActiveCardIndex = (activeCardIndex || 0) + direction;
    if (newActiveCardIndex < 0) {
      nextActiveCardIndex.current = cards.length - 1;
    } else if (newActiveCardIndex > cards.length - 1) {
      nextActiveCardIndex.current = 0;
    } else {
      nextActiveCardIndex.current = newActiveCardIndex;
    }
    setActiveCardIndex(null);
  };

  const activeCard = activeCardIndex !== null ? cards[activeCardIndex] : null;

  const isOurWork = pathname === '/our-work' || pathname === '/resources';

  return (
    <ModuleBase
      noPadding
      data={{ ...data, props: { ...data.props, backgroundColour: isOurWork ? '#1b3e4e' : '#ae2d2a' } }}
      className={classNames(
        'bg-red relative flex flex-col items-center justify-end overflow-hidden lg:h-[calc(100dvh-var(--header-height))] lg:min-h-[690px] lg:flex-row',
      )}
    >
      <Stack
        animation={animation}
        onAnimationComplete={() => setAnimation(listingGridMotion)}
        className="relative aspect-square w-full overflow-hidden md:aspect-auto lg:h-full lg:w-1/2"
      >
        <AnimatePresence>
          {activeCard && activeCard.image && (
            <Box key="image" animation={motionImage} className="relative h-full w-full">
              <ResponsiveImage
                image={activeCard.image}
                widths={{ xs: 425, sm: 768, md: 1024, lg: 720, xl: 720, xxl: 1280 }}
                heights={{ xs: 425, sm: 768, md: 1024, lg: 720, xl: 720, xxl: 1280 }}
              />
              {activeCard.imageTitle && (
                <div className="text-style-h2 xxl:text-[70px] xxl:leading-[75px] absolute bottom-0 left-0 min-h-[150px] w-full bg-black/50 p-6 text-white lg:min-h-[250px] lg:p-10 lg:text-[60px] lg:leading-[65px]">
                  {activeCard.imageTitle}
                </div>
              )}
            </Box>
          )}
        </AnimatePresence>
      </Stack>

      <div className="left-0 flex h-full w-full items-center py-10 lg:absolute lg:py-18">
        <div className="container flex h-full">
          <Stack className="color-from-bg flex w-full flex-col justify-between lg:w-1/2">
            <div className="gap-3lg:gap-5 flex flex-col">
              <AnimatePresence onExitComplete={() => setActiveCardIndex(nextActiveCardIndex.current)}>
                {activeCard && activeCard.preHeading && (
                  <Text key="preHeading" animation={motionText(0.1)} className="text-style-tag uppercase">
                    {activeCard.preHeading}
                  </Text>
                )}
                {activeCard && activeCard.title && (
                  <Text
                    key="title"
                    animation={motionText(0.2)}
                    className="text-style-h3 mt-2.5 sm:mt-4 md:pr-20"
                    dangerouslySetInnerHTML={{ __html: activeCard.title }}
                  />
                )}
              </AnimatePresence>
            </div>
            <div className="flex flex-col gap-8 sm:gap-10 xl:gap-16">
              <AnimatePresence>
                {activeCard && (
                  <Text
                    key="text"
                    animation={motionText(0.3)}
                    className="text-style-h5 mt-2 md:pr-20"
                    dangerouslySetInnerHTML={{ __html: activeCard.text }}
                  />
                )}
              </AnimatePresence>
              <div className="flex items-end justify-between pr-10">
                <div>
                  <AnimatePresence>
                    {activeCard && activeCard.link && (
                      <motion.div
                        initial={{ x: 50, opacity: 0 }}
                        animate={{
                          x: 0,
                          opacity: 1,
                          transition: {
                            delay: 0.4,
                            type: 'spring',
                            damping: 25,
                          },
                        }}
                        exit={{ x: 50, opacity: 0 }}
                        transition={{
                          type: 'tween',
                          ease: 'easeOut',
                          // duration: 0.5,
                        }}
                        key={activeCard.link.href}
                        className="flex flex-col gap-6 md:pr-12 lg:pr-16"
                      >
                        <Link
                          key="btn"
                          animate={false}
                          className={classNames(
                            'btn primary v2 btn-from-bg hover:!text-white',
                            isOurWork ? '!text-[#1b3e4e]' : '!text-red',
                          )}
                          link={activeCard.link}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <Box animation={fadeIn} className="flex gap-3">
                  <button className="cursor-pointer px-2" onClick={() => _setActiveCard(-1)}>
                    <DropdownArrow className="fill-from-bg rotate-90" />
                  </button>
                  <button className="cursor-pointer px-2" onClick={() => _setActiveCard(1)}>
                    <DropdownArrow className="fill-from-bg -rotate-90" />
                  </button>
                </Box>
              </div>
            </div>
          </Stack>
        </div>
      </div>

      <div className="hidden">
        {cards.map((card, index) => (
          <ResponsiveImage
            key={index}
            image={card.image}
            widths={{ xs: 425, sm: 768, md: 1024, lg: 720, xl: 720, xxl: 1280 }}
            heights={{ xs: 300, sm: 768, md: 1024, lg: 720, xl: 720, xxl: null }}
          />
        ))}
      </div>
    </ModuleBase>
  );
};

export default CaseStudyModule;
